import React, { useState } from "react";
import { Modal, Table, Button, message, Typography } from "antd";
import {
  CopyOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  MailOutlined,
} from "@ant-design/icons";
import "./UserCreatedResultModal.css";

const { Text } = Typography;

const UserCreatedResultModal = ({ open, onClose, users = [] }) => {
  const [copiedItems, setCopiedItems] = useState({});

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItems({ ...copiedItems, [key]: true });
      message.success("Copied to clipboard!");

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedItems((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    });
  };

  const downloadAsCSV = () => {
    const csvHeader = "Name,Email,Status,Created At\n";
    const csvRows = users
      .map(
        (user) =>
          `${user.name || "N/A"},${user.email},Invitation Sent,${
            user.created_at
          }`
      )
      .join("\n");

    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `invited_users_${new Date().getTime()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("CSV file downloaded!");
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text) => (
        <div className="flex items-center justify-between group">
          <Text className="text-sm font-medium">
            {text || <span className="text-gray-400 italic">Not provided</span>}
          </Text>
          {text && (
            <Button
              type="text"
              size="small"
              icon={
                copiedItems[`name-${text}`] ? (
                  <CheckCircleOutlined className="text-green-500" />
                ) : (
                  <CopyOutlined />
                )
              }
              onClick={() => copyToClipboard(text, `name-${text}`)}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 280,
      render: (text) => (
        <div className="flex items-center justify-between group">
          <Text className="text-sm font-medium">{text}</Text>
          <Button
            type="text"
            size="small"
            icon={
              copiedItems[`email-${text}`] ? (
                <CheckCircleOutlined className="text-green-500" />
              ) : (
                <CopyOutlined />
              )
            }
            onClick={() => copyToClipboard(text, `email-${text}`)}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 200,
      render: () => (
        <div className="flex items-center gap-2">
          <MailOutlined className="text-blue-500" />
          <Text className="text-sm text-blue-600 font-medium">
            Invitation Sent
          </Text>
        </div>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (text) => (
        <Text className="text-xs text-gray-500">
          {new Date(text).toLocaleString()}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <CheckCircleOutlined className="text-green-500 text-2xl" />
          <div>
            <div className="text-lg font-semibold">
              Users Invited Successfully
            </div>
            <div className="text-sm text-gray-500 font-normal">
              {users.length} user{users.length > 1 ? "s" : ""} invited. They will receive an email to set up their password.
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={900}
      centered
      footer={
        <div className="flex justify-end items-center gap-2">
          <Button
            icon={<DownloadOutlined />}
            onClick={downloadAsCSV}
            className="flex items-center"
          >
            Download CSV
          </Button>
          <Button
            type="primary"
            onClick={onClose}
            className="bg-blue-600"
          >
            Done
          </Button>
        </div>
      }
      className="user-created-result-modal"
    >
      <div className="py-4">
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <MailOutlined className="text-blue-600 text-xl mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-blue-900 mb-1">
                Email Invitation Sent
              </div>
              <div className="text-xs text-blue-700">
                An invitation email has been sent to each user with instructions to set up their password. 
                They will need to check their inbox and follow the link to complete their account setup.
              </div>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id_user"
          pagination={false}
          size="middle"
          scroll={{ y: 400 }}
          rowClassName="group hover:bg-gray-50 transition-colors"
          className="user-result-table"
        />
      </div>
    </Modal>
  );
};

export default UserCreatedResultModal;
